import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "react-query";
import {
  MdOutlineSync,
  MdOutlineSearch,
  MdOutlineViewKanban,
  MdOutlineWhatsapp,
} from "react-icons/md";
import axios from "../config/axiosInstance";
import { CRM, STAFF } from "../config/apiConfig";
import { useApplication } from "../contexts/ApplicationContext";
import useDebouncedValue from "../hooks/useDebouncedValue";
import {
  Badge,
  Card,
  EmptyState,
  ErrorBlock,
} from "../components/crm/Primitives";
import {
  SkeletonKpiStrip,
  SkeletonLine,
} from "../components/crm/Skeletons";
import AccountDrawer from "../components/crm/AccountDrawer";
import {
  formatDaysCount,
  formatMoney,
  formatNumber,
  formatRelativeDays,
  initialsOf,
  toneOf,
  whatsappLink,
} from "../components/crm/crmHelpers";

/*
  Pipeline do ciclo de vida.

  É um kanban de LEITURA: não existe arrastar cartão. O estágio é derivado do
  uso real da conta no servidor (admin/services/crm/lifecycle.js). Mover à mão
  criaria um estágio que envelhece sozinho e um painel que mente no dia
  seguinte.

  O que mudou depois de ver a tela com a base cheia:

  - **Barra de funil no topo.** São nove estágios e cabem quatro na largura da
    tela; sem um resumo proporcional, ninguém enxergava a forma do funil — só
    as quatro primeiras colunas.
  - **Nome em duas linhas.** "[SEED] Studio Lumi…" cortava justamente o
    identificador que a pessoa procura.
  - **Um número por cartão, não três.** Cada estágio tem UMA pergunta
    relevante: em "Novos" é há quanto tempo cadastrou; em "Free ativo" é
    quantos agendamentos fez. Três linhas de rótulo em cinza, repetidas em
    todo cartão, eram 108 repetições das mesmas três palavras numa tela.
  - **Colunas vazias encolhem.** Uma coluna de 280px dizendo "nenhuma conta
    neste estágio" custava o mesmo espaço de uma com trabalho dentro.
*/

const CARD_METRIC = {
  NEW: (card) => ({
    value: formatDaysCount(card.daysSinceRegistration),
    label: "desde o cadastro",
  }),
  ONBOARDING: (card) => ({
    value: formatNumber(card.appointmentsLast30Days),
    label: "agendamentos em 30d",
  }),
  FREE_ACTIVE: (card) => ({
    value: formatNumber(card.appointmentsLast30Days),
    label: "agendamentos em 30d",
  }),
  FREE_IDLE: (card) => ({
    value: formatDaysCount(card.daysSinceLastActivity),
    label: "parado há",
  }),
  FREE_DORMANT: (card) => ({
    value: formatDaysCount(card.daysSinceRegistration),
    label: "desde o cadastro",
  }),
  PAYING: (card) => ({
    value: formatMoney(card.monthlyValue),
    label: "por mês",
  }),
  AT_RISK: (card) => ({
    value: formatRelativeDays(card.planExpirationDate),
    label: "vence",
  }),
  CHURNED: (card) => ({
    value: formatMoney(card.totalPaid),
    label: "pagos até sair",
  }),
};

const ClientCard = ({ card, stage, isActive, onOpen }) => {
  const phone = whatsappLink(card.phoneNumber);
  const metric = (CARD_METRIC[stage] || CARD_METRIC.NEW)(card);

  return (
    <div
      className={`rounded-xl border bg-white p-2.5 shadow-sm transition ${
        isActive
          ? "border-pink-300 ring-2 ring-pink-100"
          : "border-slate-200 hover:border-pink-200 hover:shadow"
      }`}
    >
      <button
        type="button"
        onClick={() => onOpen(card.id)}
        className="flex w-full items-start gap-2 text-left"
      >
        <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-pink-50 text-[10px] font-bold text-pink-700">
          {initialsOf(card.name)}
        </span>
        <span className="min-w-0 flex-1">
          {/* Duas linhas: o nome do salão é o que a pessoa procura. */}
          <span className="line-clamp-2 text-[13px] font-bold leading-tight text-slate-900">
            {card.name}
          </span>
        </span>
      </button>

      <div className="mt-2 flex items-end justify-between gap-2">
        <span>
          <span className="block text-base font-bold leading-none text-slate-900">
            {metric.value}
          </span>
          <span className="block text-[10px] leading-tight text-slate-400">
            {metric.label}
          </span>
        </span>

        <span className="flex items-center gap-1">
          {card.openTasks > 0 ? (
            <Badge tone="info">{card.openTasks}</Badge>
          ) : null}
          {phone ? (
            <a
              href={phone}
              target="_blank"
              rel="noreferrer"
              title="Abrir WhatsApp"
              className="rounded-md p-1 text-slate-300 transition hover:bg-emerald-50 hover:text-emerald-600"
            >
              <MdOutlineWhatsapp size={15} />
            </a>
          ) : null}
        </span>
      </div>

      {/*
        O contato é a informação que decide a ordem de trabalho, então ganha
        uma linha própria. "nunca" em vermelho só quando é de fato o caso —
        pintar todos de vermelho (como na primeira versão) vira ruído e
        ninguém mais olha.
      */}
      <p
        className={`mt-1.5 border-t border-slate-100 pt-1.5 text-[10px] ${
          card.lastContactAt ? "text-slate-500" : "font-semibold text-rose-600"
        }`}
      >
        {card.lastContactAt
          ? `falado ${formatRelativeDays(card.lastContactAt)}`
          : "nunca contatada"}
        {card.ownerName ? ` · ${card.ownerName}` : ""}
      </p>
    </div>
  );
};

const Column = ({ column, openId, onOpen }) => {
  const tone = toneOf(column.tone);

  // Coluna vazia vira uma faixa estreita: continua visível (o estágio existe
  // e está zerado, que é informação) sem custar a largura de uma com trabalho.
  if (column.count === 0) {
    return (
      <div className="flex w-[52px] shrink-0 flex-col items-center rounded-2xl bg-slate-50 py-3">
        <span className={`h-1.5 w-1.5 rounded-full ${tone.dot}`} />
        <span
          className="mt-2 whitespace-nowrap text-[11px] font-semibold text-slate-400"
          style={{ writingMode: "vertical-rl" }}
        >
          {column.label} · 0
        </span>
      </div>
    );
  }

  return (
    <div className="flex w-[232px] shrink-0 flex-col rounded-2xl bg-slate-50 p-2.5">
      <div className="mb-2 px-0.5">
        <div className="flex items-center justify-between gap-2">
          <h3
            className="truncate text-[13px] font-bold text-slate-900"
            title={column.description}
          >
            {column.label}
          </h3>
          <Badge tone={column.tone}>{formatNumber(column.count)}</Badge>
        </div>

        <p className="mt-0.5 flex flex-wrap gap-x-2 text-[10px] text-slate-500">
          {column.mrr > 0 ? (
            <span className="font-semibold text-slate-700">
              {formatMoney(column.mrr)}/mês
            </span>
          ) : null}
          {column.neverContacted > 0 ? (
            <span className="text-rose-600">
              {column.neverContacted} sem contato
            </span>
          ) : null}
        </p>
      </div>

      <div className="flex-1 space-y-1.5 overflow-y-auto pr-0.5">
        {column.cards.map((card) => (
          <ClientCard
            key={card.id}
            card={card}
            stage={column.id}
            isActive={openId === card.id}
            onOpen={onOpen}
          />
        ))}

        {column.hasMore ? (
          <Link
            to={`/clientes?stage=${column.id}`}
            className="block rounded-xl border border-dashed border-slate-300 py-2 text-center text-[10px] font-semibold text-slate-500 transition hover:border-pink-300 hover:text-pink-700"
          >
            ver as {formatNumber(column.count)} na tabela
          </Link>
        ) : null}
      </div>
    </div>
  );
};

/*
  Resumo proporcional do funil. É a leitura que o kanban não dá: com nove
  colunas e quatro visíveis, a forma da base ficava escondida atrás da rolagem
  horizontal. Clicar num segmento rola até a coluna.
*/
const FunnelBar = ({ columns, total }) => (
  <div>
    <div className="flex h-7 w-full overflow-hidden rounded-xl">
      {columns
        .filter((column) => column.count > 0)
        .map((column) => (
          <button
            key={column.id}
            type="button"
            title={`${column.label}: ${column.count} (${column.description})`}
            onClick={() =>
              document
                .getElementById(`col-${column.id}`)
                ?.scrollIntoView({ behavior: "smooth", inline: "start", block: "nearest" })
            }
            className={`group relative flex items-center justify-center transition hover:brightness-95 ${
              toneOf(column.tone).bar
            }`}
            style={{ width: `${(column.count / Math.max(total, 1)) * 100}%` }}
          >
            <span className="px-1 text-[10px] font-bold text-white/90">
              {column.count}
            </span>
          </button>
        ))}
    </div>

    <div className="mt-1.5 flex flex-wrap gap-x-3 gap-y-1">
      {columns
        .filter((column) => column.count > 0)
        .map((column) => (
          <span
            key={column.id}
            className="flex items-center gap-1 text-[11px] text-slate-600"
          >
            <span
              className={`h-2 w-2 rounded-full ${toneOf(column.tone).dot}`}
            />
            {column.label}
          </span>
        ))}
    </div>
  </div>
);

const PipelinePage = () => {
  const queryClient = useQueryClient();
  const { isAdmin } = useApplication();
  const [search, setSearch] = useState("");
  const [ownerUserId, setOwnerUserId] = useState("");
  const [openId, setOpenId] = useState(null);
  const debouncedSearch = useDebouncedValue(search, 400);

  const { data: owners } = useQuery(
    ["staff", "options"],
    async () => {
      const { data } = await axios.get(STAFF.OPTIONS);
      return data.data;
    },
    { enabled: isAdmin }
  );

  const pipelineQuery = useQuery(
    ["crm", "pipeline", debouncedSearch, ownerUserId],
    async () => {
      const params = new URLSearchParams();
      if (debouncedSearch) params.set("search", debouncedSearch);
      if (ownerUserId) params.set("ownerUserId", ownerUserId);

      const { data } = await axios.get(`${CRM.PIPELINE}?${params.toString()}`);
      return data.data;
    },
    { keepPreviousData: true }
  );

  const syncStages = useMutation(
    async () => {
      const { data } = await axios.post(CRM.PIPELINE_SYNC);
      return data.data;
    },
    {
      onSuccess: () => queryClient.invalidateQueries(["crm"]),
    }
  );

  const data = pipelineQuery.data;

  // Navegação do painel percorre os cartões na ordem visual das colunas.
  const flatCards = useMemo(
    () => (data?.columns || []).flatMap((column) => column.cards),
    [data]
  );
  const openIndex = flatCards.findIndex((card) => card.id === openId);

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-slate-900">Pipeline</h1>
          <p className="text-sm text-slate-500">
            Estágio derivado do uso real — não é movido à mão.
            {data ? (
              <>
                {" "}
                <span className="font-semibold text-slate-700">
                  {formatNumber(data.totals.companies)} contas
                </span>
                , {formatNumber(data.totals.neverContacted)} nunca contatadas.
              </>
            ) : null}
          </p>
        </div>

        {isAdmin ? (
          <button
            type="button"
            onClick={() => syncStages.mutate()}
            disabled={syncStages.isLoading}
            className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 shadow-sm transition hover:border-pink-200 disabled:opacity-50"
          >
            <MdOutlineSync
              size={16}
              className={syncStages.isLoading ? "animate-spin" : ""}
            />
            {syncStages.isLoading ? "Reclassificando..." : "Reclassificar base"}
          </button>
        ) : null}
      </header>

      {syncStages.isSuccess && syncStages.data ? (
        <p className="rounded-xl bg-emerald-50 px-3 py-2 text-xs font-semibold text-emerald-800 ring-1 ring-inset ring-emerald-200">
          {formatNumber(syncStages.data.checked)} contas verificadas,{" "}
          {formatNumber(syncStages.data.moved)} mudaram de estágio.
        </p>
      ) : null}

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative min-w-[220px] flex-1">
          <MdOutlineSearch
            size={18}
            className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
          />
          <input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Buscar por nome, e-mail, telefone, documento ou #id"
            className="w-full rounded-xl border border-slate-200 bg-white py-2 pl-10 pr-3 text-sm shadow-sm outline-none focus:border-pink-300 focus:ring-2 focus:ring-pink-100"
          />
        </div>

        {isAdmin ? (
          <select
            value={ownerUserId}
            onChange={(event) => setOwnerUserId(event.target.value)}
            className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm shadow-sm outline-none focus:border-pink-300"
          >
            <option value="">Toda a base</option>
            <option value="none">Sem responsável</option>
            {(owners || []).map((owner) => (
              <option key={owner.id} value={owner.id}>
                {owner.name}
              </option>
            ))}
          </select>
        ) : null}
      </div>

      {pipelineQuery.error ? (
        <ErrorBlock
          message="Não foi possível carregar o pipeline."
          onRetry={pipelineQuery.refetch}
        />
      ) : pipelineQuery.isLoading ? (
        <>
          <SkeletonKpiStrip count={3} withIcon={false} />
          <div className="flex gap-2.5 overflow-hidden">
            {Array.from({ length: 6 }).map((_, index) => (
              <div
                key={`col-skeleton-${index}`}
                className="w-[232px] shrink-0 space-y-2 rounded-2xl bg-slate-50 p-2.5"
              >
                <SkeletonLine className="h-3 w-28" />
                {Array.from({ length: 4 }).map((__, card) => (
                  <div
                    key={`card-${index}-${card}`}
                    className="space-y-2 rounded-xl border border-slate-200 bg-white p-2.5"
                  >
                    <SkeletonLine className="h-3 w-36" />
                    <SkeletonLine className="h-4 w-16" />
                    <SkeletonLine className="h-2 w-28" />
                  </div>
                ))}
              </div>
            ))}
          </div>
        </>
      ) : data?.totals.companies === 0 ? (
        <Card>
          <EmptyState
            icon={MdOutlineViewKanban}
            title="Nenhuma conta no recorte"
            description="Limpe a busca ou troque o responsável selecionado."
          />
        </Card>
      ) : (
        <>
          <FunnelBar columns={data.columns} total={data.totals.companies} />

          <div className="flex h-[calc(100vh-20rem)] min-h-[420px] gap-2.5 overflow-x-auto pb-2">
            {data.columns.map((column) => (
              <div key={column.id} id={`col-${column.id}`} className="flex">
                <Column column={column} openId={openId} onOpen={setOpenId} />
              </div>
            ))}
          </div>
        </>
      )}

      {openId ? (
        <AccountDrawer
          companyId={openId}
          onClose={() => setOpenId(null)}
          position={openIndex >= 0 ? openIndex + 1 : null}
          total={flatCards.length}
          onPrev={
            openIndex > 0
              ? () => setOpenId(flatCards[openIndex - 1].id)
              : undefined
          }
          onNext={
            openIndex >= 0 && openIndex < flatCards.length - 1
              ? () => setOpenId(flatCards[openIndex + 1].id)
              : undefined
          }
        />
      ) : null}
    </div>
  );
};

export default PipelinePage;
